import React, {useCallback, useEffect, useState} from 'react';

import {Banner, Icon, Text, Anchor} from '@albert-io/atomic';
import type {ClassroomModelV1} from '@albert-io/models';
import systemTimeOffsetStore from 'generic/SystemTimeOffset/SystemTimeOffset.store';
import {renderMoment} from 'lib/timeUtils';

import {isAutoArchivingSoon} from '../../ClassDetail/AutoArchiveChip';
import {AUTO_ARCHIVE_DETAIL_COPY} from '../../ClassDetail/autoArchiveCopy';
import {useTeacherClassrooms} from '../../hooks/useTeacherClassrooms';

/** The yearly auto-archive runs July 1; the "recently archived" notice shows July 1–15. */
const getSeasonBounds = () => {
  const now = systemTimeOffsetStore.getCurrentTime();
  // July is month index 6.
  return {
    start: now.clone().month(6).date(1).startOf('day'),
    end: now.clone().month(6).date(15).endOf('day')
  };
};

const isAutoArchiveSeason = (): boolean => {
  const {start, end} = getSeasonBounds();
  // Inclusive bounds — default isBetween is exclusive and would drop July 1 at midnight.
  return systemTimeOffsetStore.getCurrentTime().isBetween(start, end, undefined, '[]');
};

/**
 * Whether a class was auto-archived in THIS season — its auto-archive date (always July 1, per mandark)
 * falls in the current window. Excludes classes archived in prior years.
 */
const wasAutoArchivedThisSeason = (classroom: ClassroomModelV1): boolean => {
  const date = classroom.getAutoArchiveDate?.();
  if (!date) {
    return false;
  }
  const {start, end} = getSeasonBounds();
  // auto_archive_date is a calendar date mandark serializes as UTC midnight (e.g. 2026-07-01T00:00:00Z).
  // Compare it as a UTC calendar day against the season window
  const archiveDay = date.clone().utc().format('YYYY-MM-DD');
  return start.format('YYYY-MM-DD') <= archiveDay && archiveDay <= end.format('YYYY-MM-DD');
};

export const AutoArchiveBanner = () => {
  const {archivedClassrooms, classrooms, fetchArchivedClassrooms} = useTeacherClassrooms();
  const [expanded, setExpanded] = useState(false);
  // Stable identity → runs only on the span's mount, not every banner re-render.
  const focusDetailOnMount = useCallback((node: HTMLSpanElement | null) => node?.focus(), []);

  const season = isAutoArchiveSeason();

  // The "were archived" notice is about classes that were just auto-archived — those live in the
  // archived list, which is otherwise only fetched lazily (via the sidemenu's "Show Archived"). Pull it
  // in during the season window so the notice reflects whether THIS teacher actually had classes archived.
  useEffect(() => {
    if (season && archivedClassrooms.isEmpty()) {
      fetchArchivedClassrooms();
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [season]);

  const soonest = season
    ? null
    : classrooms
        .filter((classroom) => isAutoArchivingSoon(classroom))
        .minBy((classroom) => classroom.getAutoArchiveDate().valueOf());

  // During the season, only count classes auto-archived in THIS run — not ones archived in prior years.
  const hasArchivedThisSeason =
    season && archivedClassrooms.some((c) => wasAutoArchivedThisSeason(c));

  // Outside the season: show only when a class is auto-archiving soon (active list). During the season:
  // show only if the teacher had a class auto-archived this season. Active and archived are separate
  // lists, so the old `classrooms.isEmpty()` check was wrong on both ends.
  if ((!season && !soonest) || (season && !hasArchivedThisSeason)) {
    return null;
  }

  const title = soonest
    ? // Render in UTC: auto_archive_date is a calendar date stored as UTC midnight
      `Classes will be automatically archived on ${renderMoment(
        soonest.getAutoArchiveDate(),
        'dateFormalShortWithFullMonth',
        'UTC'
      )}`
    : 'Classes were automatically archived';

  return (
    <Banner alignContent='center' type={soonest ? 'warning' : 'info'}>
      <div className='gap-x2 flex grow items-center'>
        <Icon color='inherit' icon='box-archive' iconStyle='regular' />
        <div className='flex flex-col items-start'>
          <Text bold color='inherit'>
            {title}
          </Text>
          {expanded ? (
            // The trigger unmounts on expand (per design); focus the copy so it isn't dropped to <body>.
            <span ref={focusDetailOnMount} tabIndex={-1} className='outline-none'>
              <Text color='inherit'>{AUTO_ARCHIVE_DETAIL_COPY}</Text>
            </span>
          ) : (
            <Anchor
              noUnderline
              className='text-inherit'
              onClick={() => setExpanded(true)}
              as='button'
            >
              Show more info
            </Anchor>
          )}
        </div>
      </div>
    </Banner>
  );
};
